import { GenericDropdownOption } from 'App.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NamedProps, ValueType } from 'react-select';
import { debounce } from 'lodash';

export type SelectProps<TOption extends GenericDropdownOption<any>, TIsMulti extends boolean> = Pick<
    NamedProps<TOption, TIsMulti>,
    | 'onMenuOpen'
    | 'onMenuClose'
    | 'onMenuScrollToBottom'
    | 'inputValue'
    | 'onInputChange'
    | 'value'
    | 'onChange'
    | 'filterOption'
    | 'isLoading'
    | 'noOptionsMessage'
    | 'isMulti'
    | 'options'
    | 'isSearchable'
    | 'isClearable'
    | 'isDisabled'
    | 'formatOptionLabel'
    | 'styles'
    | 'menuPlacement'
    | 'onKeyDown'
>;

type Return<TOption extends GenericDropdownOption<any>, TIsMulti extends boolean> = {
    getProps: (
        overrides?: Partial<Omit<SelectProps<TOption, TIsMulti>, 'value' | 'inputValue'>>
    ) => SelectProps<TOption, TIsMulti>;
    debouncedSearchValue: string;
    isMenuOpen: boolean;
    resetValue: () => void;
    setValue: (value: ValueType<TOption, TIsMulti>) => void;
};

type Params<TOption extends GenericDropdownOption<any>, TIsMulti extends boolean> = {
    initialValue?: ValueType<TOption, TIsMulti>;
};

export default function useSelectProps<TOption extends GenericDropdownOption<any>, TIsMulti extends boolean>({
    initialValue,
}: Params<TOption, TIsMulti> = {}): Return<TOption, TIsMulti> {
    const [value, setValue] = useState<ValueType<TOption, TIsMulti>>(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const [search, setSearch] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const setDebouncedSearch = useMemo(() => debounce(setDebouncedSearchValue, 500), []);

    useEffect(() => () => setDebouncedSearch.cancel(), [setDebouncedSearch]);

    return {
        getProps: (overrides) => ({
            ...overrides,
            value,
            inputValue: search,
            onChange: (value, action) => {
                setValue(value);
                overrides?.onChange?.(value, action);
            },
            onInputChange: (val, actionMeta) => {
                setSearch(val);
                setDebouncedSearch(val);
                overrides?.onInputChange?.(val, actionMeta);
            },
            onMenuOpen: () => {
                setIsMenuOpen(true);
                overrides?.onMenuOpen?.();
            },
            onMenuClose: () => {
                setIsMenuOpen(false);
                overrides?.onMenuClose?.();
            },
            onMenuScrollToBottom: (e) => {
                overrides?.onMenuScrollToBottom?.(e);
            },
        }),
        isMenuOpen,
        debouncedSearchValue,
        resetValue: useCallback(() => setValue(null), []),
        setValue,
    };
}
